<template>
  <div ref="wrapperEl" :class="wrapperClasses">
    <OpenDropdownIcon class="main-open-icon"></OpenDropdownIcon>
    <label :for="name">{{ label }}</label>
    <input
      type="text"
      readonly
      v-model="localVal.key"
      @click="open = !open"
    />
    <transition name="slide">
      <div
        class="all-options-wrapper"
        v-if="open"
        ref="optionsContainer"
      >
        <SearchInput
          ref="searchInput"
          class="search-input"
          v-model="searchTerm"
          :name="name"
          :placeholder="'Search'"
        >
          <ClearIcon
            v-if="searchTerm"
            class="clear"
            @click.native="searchTerm = ''"
          ></ClearIcon>
        </SearchInput>
        <div
          class="dropdown-content"
          :class="complementDropdownClass"
        >
          <transition name="showOptions">
            <div v-if="showOptions" class="options-list">
              <div class="back-link">
                <Anchor
                  @click="goBack"
                >
                  <template v-slot:icon>
                    <PageLeftIcon/>
                  </template>
                  Back
                </Anchor>
              </div>
              <div v-if="filteredOptions.categoryOptions.length">
                <ul class="options">
                  <li
                    v-for="option in filteredOptions.categoryOptions"
                    :class="getOptionClasses(option)"
                    :key="option.value"
                    :value="option.value"
                    :id="generateOptionId(option.value)"
                    @click="handleSelect($event, option)"
                  >
                    {{ getOptionValue(option) }}
                  </li>
                </ul>
              </div>
              <div v-else class="options no-results">No Results Found</div>
            </div>
          </transition>
          <div
            v-if="sections.length"
            :class="displayStatus">
            <ul
              class="categories"
              v-for="section in sections"
              :key='"section-"+section'
            >
              <li
                v-if="showCheckbox && !searchTerm"
                :class="checkboxClasses"
                @click="handleCheckbox"
              >
                <Checkbox
                  class="checkbox"
                  :value="selectAll.checked"
                  :name="selectAll.key"
                  :disabled="isCheckboxInactive"
                >
                  {{ checkboxTitle }}
                </Checkbox>
              </li>
              <li
                class="section"
                :class="complementSectionClass"
                :key='"sectionTitle-"+section'
                v-if='$slots[section]'
              >
                <slot :name="section"></slot>
              </li>
              <li
                class="section"
                :class="complementSectionClass"
                :key='"sectionTitle-"+section'
                v-else
              >
                <AlertIcon class="section-icon"/>
                {{section}}
              </li>
              <li
                v-for="category in filteredCategories[section]"
                :key='section+"category-"+category'
                :class="['category', { disabled: selectAll.checked }]"
                :value="category"
                @click="openCategory(section,category)"
              >
                <div class="category-name">{{ category }}</div>
                <TriangleArrowRightIcon :color="categoryIconColor"/>
              </li>
            </ul>
          </div>
          <div v-else class="options no-results">No Results Found</div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import {
  OpenDropdownIcon,
  SearchInput,
  ClearIcon,
  PageLeftIcon,
  TriangleArrowRightIcon,
  AlertIcon,
  Anchor,
  Checkbox,
} from '@mikmak/components';

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    label: {
      type: String,
      require: true,
    },
    show: {
      type: Boolean,
    },
    optionsList: {
      type: Array,
      required: true,
      validator: val => {
        return val.every(v => !!v.key && !!v.value && !!v.category && !!v.section);
      },
    },
    required: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    selectAllCheckbox: {
      type: Object,
    },
    hideSectionTitles: {
      type: Boolean,
      default: false,
    },
    isCheckboxInactive: {
      type: Boolean,
      deafult: false,
    },
  },
  data() {
    return {
      open: this.show,
      searchTerm: '',
      selectedSection: null,
      selectedCategory: null,
      defaultSection: 'other categories',
      sortedShopperCategories: [
        'Demographics',
        'Household',
        'Lifestyles & Interests',
        'Home & Real Estate',
        'Charitable Donations',
        'Epsilon Niches',
      ],
      selectAll: {},
    };
  },
  components: {
    OpenDropdownIcon,
    SearchInput,
    ClearIcon,
    PageLeftIcon,
    TriangleArrowRightIcon,
    AlertIcon,
    Anchor,
    Checkbox,
  },
  computed: {
    showCheckbox() {
      return !!(this.selectAll.key && this.selectAll.value);
    },
    checkboxTitle() {
      return this.selectAll.checkboxLabel ||  this.selectAll.key;
    },
    displayStatus() {
      return this.showOptions ? 'display-none': 'category-list';
    },
    showOptions() {
      return !!this.selectedCategory;
    },
    sections() {
      const sections = Object.keys(this.filteredCategories);
      const defaultSectionIndex = sections.indexOf(this.defaultSection);

      if (defaultSectionIndex >= 0) {
        sections.splice(defaultSectionIndex, 1);
        sections.push(this.defaultSection);
      }

      return sections.sort((a, _) => {
        if ( a.toLowerCase().includes('mikmak')) return -1;

        return 1;
      });
    },
    filteredCategories() {
      const filteredCategories = this.filteredOptions.searchOptions.reduce((acc, option) => {
        const category = option.category;
        const section = option.section;

        if (!acc[section]) acc[section] = [category];
        else if (!acc[section].includes(category)) acc[section].push(category);

        return acc;
      }, {});

      Object.keys(filteredCategories)
        .forEach(category => {
          if (category === 'mikmakShopperIntelligence') {
            filteredCategories[category].sort((a, b) => {
              return this.sortedShopperCategories
                .indexOf(a) - this.sortedShopperCategories.indexOf(b);
            });
          } else {
            filteredCategories[category].sort((a, b) => a.localeCompare(b));
          }
        });

      return filteredCategories;
    },
    filteredOptions() {
      const localOptionsFiltered = this.localOptions.reduce((acc, option) => {
        const valueMatchsSearch = !this.lowerCaseSearchTerm ||
            option.key.toLowerCase().indexOf(this.lowerCaseSearchTerm) > -1;
        const categoryMatchsSearch = !this.lowerCaseSearchTerm ||
            (option.category &&
            option.category.toLowerCase().indexOf(this.lowerCaseSearchTerm) > -1);

        if (valueMatchsSearch || categoryMatchsSearch) {
          acc.searchOptions.push(option);
        }

        if (this.selectedCategory) {
          const hasSelectedCategory = option.category === this.selectedCategory;
          const hasSelectedSection = option.section === this.selectedSection;

          const isFiltered = (categoryMatchsSearch || valueMatchsSearch) &&
            hasSelectedCategory &&
            hasSelectedSection;

          if (isFiltered) acc.categoryOptions.push(option);
        }

        return acc;
      }, { categoryOptions: [], searchOptions: [] });

      return localOptionsFiltered;
    },
    localOptions() {
      return this.optionsList
        .slice()
        .sort((a, b) => a.key.localeCompare(b.key))
        .map(option => {
          option.section = this.$slots[option.section] ? option.section : this.defaultSection;

          return option;
        });
    },
    lowerCaseSearchTerm() {
      return this.searchTerm.toLowerCase();
    },
    localVal() {
      if (this.isValueBlank(this.value)) return {};
      const matchingOption = this.optionsListExtended
        .find(o => o.value === this.value);

      return matchingOption || {};
    },
    optionsListExtended() {
      return this.showCheckbox ?
        this.optionsList.concat([this.selectAll]) :
        this.optionsList;
    },
    wrapperClasses() {
      let classes = 'wrapper';

      if (!this.isValueBlank(this.value)) classes += ' active';
      if (this.open) classes += ' open';
      if (this.required && !this.value) classes += ' highlight';

      return classes;
    },
    complementDropdownClass() {
      return this.small ? 'small' : '';
    },
    complementSectionClass() {
      return this.hideSectionTitles ? 'hide' : '';
    },
    categoryIconColor() {
      return this.selectAll.checked ? '#778699' : '#223448';
    },
    checkboxClasses() {
      return {
        'checkbox-wrapper': true,
        'inactive-checkbox': this.isCheckboxInactive,
      };
    },
  },
  methods: {
    getOptionClasses(option) {
      return {
        'option': true,
        'selected': this.value === option.value,
        'inactive': option.disabled,
      };
    },
    isValueBlank(val) {
      return val !== false && val !== 0 && !val;
    },
    goBack() {
      this.selectedSection = null;
      this.selectedCategory = null;
    },
    openCategory(section, category) {
      if (!this.selectAll.checked) {
        this.selectedSection = section;
        this.selectedCategory = category;
      }
    },
    generateOptionId(value) {
      return `${String(value)
        .split(' ')
        .join('-')}-opt`;
    },
    handleSelect(_, option) {
      this.$emit('input', option.value);
      this.goBack();
      this.open = false;
    },
    externalClickHandler(e) {
      if (
        this.$refs.wrapperEl &&
        !this.$refs.wrapperEl.contains(e.target) &&
        this.open
      ) {
        this.open = false;
      }
    },
    setUpExternalClickListeners() {
      document.addEventListener('click', this.externalClickHandler, true);
      document.addEventListener('touchstart', this.externalClickHandler, true);
    },
    focusSearchInput() {
      this.$refs.searchInput.focus();
    },
    handleCheckbox() {
      this.selectAll.checked = !this.selectAll.checked;
      this.$emit('input', this.selectAll.checked ? this.selectAll.value : '');
      if (this.selectAll.checked) this.open = false;
    },
    getOptionValue(option) {
      let label = option.key;

      if (option.disabled) label += ' (Not available)';

      return label;
    },
  },
  mounted() {
    this.setUpExternalClickListeners();

    if (this.selectAllCheckbox) {
      this.selectAll = { ...this.selectAllCheckbox };
      this.selectAll.checked = this.value === this.selectAll.value;
    }
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.$nextTick(this.focusSearchInput);
      }
    },
    selectAllCheckbox() {
      this.selectAll = { ...this.selectAllCheckbox };
      this.selectAll.checked = this.value === this.selectAll.value;
    },
  },
};
</script>
<style lang="scss" scoped>
.wrapper {
  display: inline-block;
  width: 100%;
  font-family: Lato;
  position: relative;
  font-size: 16px;
  line-height: 16px;
}
.wrapper.active label {
  font-size: 12px;
  line-height: 14px;
  transform: translate(0, -100%);
  top: 46%;
}
.wrapper label {
  font-family: Lato;
  color: #778699;
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translate(0, -50%);
  transition: 0.25s ease;
  cursor: pointer;
  pointer-events: none;
}
.wrapper input[type="text"] {
  font-family: Lato;
  background: #FFFFFF;
  border: 1.5px solid #C1CCD4;
  color: #223448;
  box-sizing: border-box;
  border-radius: 4px;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  font-size: 16px;
  line-height: 19px;
  transition: border 0.5s ease;
  cursor: pointer;
}
.wrapper.highlight input:read-only[type="text"] {
  border: 1.5px solid #CB4444;
}
.wrapper.open.highlight input:read-only[type="text"] {
  border: 1.5px solid #CB4444;
}
.wrapper.open:not(.highlight) input:read-only[type="text"] {
  border: 1.5px solid #778699;
}
.wrapper input[type="text"]:focus,
.wrapper.open input[type="text"] {
  outline: none;
  border-color: #778699;
}

.active input[type="text"] {
  padding-top: 16px;
}
.open input[type="text"] {
  border-radius: 4px 4px 0 0;
}
.wrapper .main-open-icon {
  position: absolute;
  right: 0;
  margin: 10px;
}
.wrapper .main-open-icon,
.wrapper .second-open-icon {
  cursor: pointer;
  transition: all 0.25s ease;
  pointer-events: none;
}
.wrapper.open .main-open-icon,
.wrapper .open .second-open-icon {
  transform: rotate(-180deg);
}
.clear {
  right: 12px;
  cursor: pointer;
}
.search-input::v-deep {
  .input-wrapper > input[type="text"] {
    border-radius: 0;
    border-right: none;
    border-left: none;
    border-top: none;
  }
}
.all-options-wrapper {
  z-index: 2;
  text-align: left;
  background: #FFFFFF;
  position: absolute;
  border: 1.5px solid #778699;
  border-top: none;
  border-radius: 0 0 4px 4px;
  transform-origin: top;
  transition: transform 0.25s ease-in-out;
  min-width: 200px;
  width: auto;
  display: inline-block;
  right: 0;
  left: 0;
  top: 44px;
  padding: 0;
  margin: 0;
}
.dropdown-content {
  height: 470px;
  width: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
}
.dropdown-content.small {
  height: 300px;
}
.options-list,
.category-list{
  width: 100%;
  height: 100%;
  background-color: #FFFFFF;
}
.display-none {
  display: none;
}
.showOptions-enter-active,
.showOptions-leave-enter {
    transform: translateX(0);
    transition: transform 0.25s ease-in-out;
}
.showOptions-enter,
.showOptions-leave-to {
    transform: translateX(100%);
    transition: transform 0.25s ease-in-out;
}
.section-title {
  height: 16px;
  padding: 14px;
  color: #223448;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
}
.options, .categories {
  text-align: left;
  background: #FFFFFF;
  transform-origin: top;
  transition: transform 0.25s ease-in-out;
  overflow-y: auto;
  width: 100%;
  display: inline-block;
  padding: 0;
  margin: 0;
}
.option {
  cursor: pointer;
  height: 20px;
  padding: 8px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #223448;
}
.option:hover,
.option.active {
  background: #EDF3F7;
}
.option:last-child:hover {
  border-radius: 0 0 4px 4px;
}
.checkbox-wrapper {
  height: 20px;
  position: relative;
  padding: 10px 0px 8px 0px;
  cursor: pointer;
}
.inactive-checkbox {
  pointer-events: none;
}
.wrapper .inactive-checkbox label.checkbox{
  color: #c1ccd4;
}
.checkbox-wrapper:hover {
  background: #EDF3F7;
}
.wrapper label.checkbox {
  cursor: pointer;
  height: 20px;
  white-space: nowrap;
  overflow: hidden;
  color: #223448;
  left: 8px;
  width: 100%;
  transform: none;
  top: auto;
}
.section {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 18px;
  padding: 8px 12px;
  padding-top: 10px;
  width: calc(100% - 28px);
  color: #223448;
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.section.hide {
  display: none;
}
.section-border {
  border-top: 1px solid #778699;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.section-border span {
  margin-left: 14px;
}
::v-deep {
  & .section-icon{
    padding-right: 8px;
  }
}
.category {
  height: 20px;
  padding: 8px 12px;
  width: calc(100% - 28px);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #778699;
  .category-name {
    height: inherit;
    width: 80%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: capitalize;
  }
}
.category:not(.disabled) {
  cursor: pointer;
  color: #223448;
}
.category:not(.disabled):hover,
.category.active:not(.disabled) {
  background: #EDF3F7;
}
.category:last-child:hover {
  border-radius: 0 0 4px 4px;
}
.slide-enter,
.slide-leave-to {
  transform: scaleY(0) translateZ(0);
}
.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #778699;
  height: 485px;
}
.inactive {
  pointer-events: none;
  color: #C1CCD4;
}
</style>
